import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    ListItem,
    ListItemText,
    Skeleton,
    TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useInternalSkuContext } from "../Contexts/internalSkuContext";
import { useDistributorsContext } from "../Contexts/distributorsContext";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const formatInternalSkuLabel = (option) => {
    if (typeof option === "string") return option;
    return `${option?.internalSku ?? ""} - ${option?.sapId ?? ""} - ${option?.sku ?? ""}`;
};

const getInternalSkuOptionKey = (opt) =>
    `${opt?.internalSku ?? ""}-${opt?.sapId ?? ""}-${opt?.sku ?? ""}`;

const isSameInternalSkuOption = (a, b) => {
    if (typeof a !== "object" || typeof b !== "object") return a === b;
    return (
        a?.internalSku === b?.internalSku &&
        a?.sapId === b?.sapId &&
        a?.sku === b?.sku
    );
};

const internalSkuOptionMatchesSearch = (opt, searchInput) => {
    const input = (searchInput || "").toLowerCase();
    return (
        (opt.internalSku || "").toLowerCase().includes(input) ||
        (opt.sapId || "").toLowerCase().includes(input) ||
        (opt.sku || "").toLowerCase().includes(input)
    );
};

export default function InternalSkuMultiSelection() {
    const {
        internalSkus,
        isReady,
        selectedInternalSkus,
        setSelectedInternalSkus,
    } = useInternalSkuContext();
    const { selectedDistributors } = useDistributorsContext();

    const [inputValue, setInputValue] = useState("");
    const [selectAll, setSelectAll] = useState(false);

    const options = internalSkus || [];
    const selectedValues = options.filter((opt) =>
        selectedInternalSkus.some((s) => isSameInternalSkuOption(opt, s))
    );

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedInternalSkus([]);
        } else {
            const filtered = options.filter((opt) =>
                internalSkuOptionMatchesSearch(opt, inputValue)
            );
            setSelectedInternalSkus(filtered);
        }
        setSelectAll(!selectAll);
    };

    const handleChange = (_event, value) => {
        const next = value.filter((v) => typeof v === "object" && v !== null);
        setSelectedInternalSkus(next);
        setSelectAll(next.length === options.length);
    };

    if (!isReady) {
        return (
            <Skeleton variant="rectangular" width={350} height={50} />
        );
    }

    return (
        <Autocomplete
            multiple
            limitTags={1}
            disablePortal
            filterOptions={(opts, state) => {
                const input = state.inputValue || "";
                const matching = opts.filter((opt) =>
                    internalSkuOptionMatchesSearch(opt, input)
                );
                matching.unshift("Select All");
                return matching;
            }}
            id="combo-box-internal-sku"
            getOptionLabel={formatInternalSkuLabel}
            options={options}
            disableCloseOnSelect
            inputValue={inputValue}
            isOptionEqualToValue={isSameInternalSkuOption}
            onInputChange={(event, value, reason) => {
                if (event?.type === "blur") {
                    setInputValue("");
                } else if (reason !== "reset") {
                    setInputValue(value);
                }
            }}
            renderOption={(props, option) => {
                if (option === "Select All") {
                    return (
                        <ListItem
                            {...props}
                            key="select-all"
                            onClick={handleSelectAllChange}
                        >
                            <Checkbox checked={selectAll} />
                            <ListItemText primary="Select All" />
                        </ListItem>
                    );
                }

                const isSelected = selectedInternalSkus?.some((s) =>
                    isSameInternalSkuOption(option, s)
                );

                return (
                    <ListItem {...props} key={getInternalSkuOptionKey(option)}>
                        <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={isSelected}
                        />
                        <ListItemText primary={formatInternalSkuLabel(option)} />
                    </ListItem>
                );
            }}
            sx={{
                width: 350,
                "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
                    paddingRight: "20px",
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Customer Item #"
                />
            )}
            renderTags={(value, getTagProps) => {
                const visibleChips = value.slice(0, 1);
                const hiddenCount = value.length - visibleChips.length;
                return (
                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="nowrap"
                        overflow="hidden"
                    >
                        {visibleChips?.map((option, index) => (
                            <Chip
                                label={formatInternalSkuLabel(option)}
                                sx={{ width: "210px" }}
                                {...getTagProps({ index })}
                                key={typeof option === "object" ? getInternalSkuOptionKey(option) : option}
                            />
                        ))}
                        {hiddenCount > 0 && (
                            <Chip label={`+${hiddenCount}`} />
                        )}
                    </Box>
                );
            }}
            value={selectedValues}
            onChange={handleChange}
            disabled={!selectedDistributors?.length}
        />
    );
}
